import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { Header } from "../components/Sidebar";
import { StatusBadge } from "../components/StatusBadge";
import { ComprobanteModal } from "../components/ComprobanteModal";
import { ConfirmFlete } from "../components/ConfirmFlete";
import { useAuth } from "../context/AuthContext";
import api, { apiErr } from "../lib/api";
import { formatGs, waLink, openWa, MODALIDADES, STATUS_META, avisoEntregaRemitente, pyToday } from "../lib/format";
import { matchQ } from "../components/SearchBox";
import { MapPin, MessageCircle, Wallet, PackageCheck, Truck, CheckCircle2, BadgeDollarSign, Zap, AlertTriangle, Building2, Flag } from "lucide-react";
import { IncidenciaModal } from "../components/IncidenciaModal";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";

const MODALIDAD_LABEL = Object.fromEntries(MODALIDADES.map((m) => [m.id, m.label]));

export default function MiRuta() {
  const { setMobileOpen } = useOutletContext();
  const { user } = useAuth();
  const [envios, setEnvios] = useState([]);
  const [retiros, setRetiros] = useState([]);
  const [stats, setStats] = useState(null);
  const [comp, setComp] = useState(null);
  const [inc, setInc] = useState(null);
  const [cfRetiro, setCfRetiro] = useState(null);
  const [depSel, setDepSel] = useState([]);
  const [dia, setDia] = useState(null);
  const [q, setQ] = useState("");
  const isDelivery = ["repartidor", "comisionista"].includes(user?.role);

  const load = useCallback(() => {
    api.get("/envios").then(({ data }) => setEnvios(data)).catch(() => {});
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/retiros").then(({ data }) => setRetiros(data.filter((r) => r.status !== "completado"))).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    const onFocus = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    const interval = setInterval(onFocus, 30000);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
      clearInterval(interval);
    };
  }, [load]);

  const today = pyToday();
  const activosTodos = envios.filter((e) => ["asignado_repartidor", "en_reparto"].includes(e.status)).filter(matchQ(q));
  const activosMios = activosTodos.filter((e) => e.repartidor_id === user?.id);
  const activosOtros = isDelivery ? [] : activosTodos.filter((e) => e.repartidor_id !== user?.id);
  const activos = [...activosMios, ...activosOtros];
  const misIncidencias = envios.filter((e) => e.status === "incidente" && e.repartidor_id === user?.id);
  const retiroEnvios = envios.filter((e) => e.repartidor_retiro_id === user?.id && e.status === "solicitado");
  const paraDepositar = envios.filter((e) => e.repartidor_retiro_id === user?.id && e.status === "retirado");
  const entregadosHoy = envios.filter((e) => e.status === "entregado" && (e.entregado_at || "").slice(0, 10) === today);
  const cobradoHoy = entregadosHoy.reduce((s, e) => s + (e.monto_cobrado || 0), 0);

  const onRetiroConfirmado = async (env) => {
    if (env.status === "solicitado") {
      try { await api.post(`/envios/${env.id}/status`, { status: "retirado", nota: "Retirado del remitente" }); } catch (e2) { toast.error(apiErr(e2)); }
    }
    load();
  };

  const tomar = async (e) => {
    try { await api.post(`/envios/${e.id}/status`, { status: "en_reparto" }); toast.success("En reparto"); load(); }
    catch (e2) { toast.error(apiErr(e2)); }
  };
  const toggleDep = (id) => setDepSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const depositar = async () => {
    if (depSel.length === 0) return;
    try {
      const { data } = await api.post("/envios/bulk-status", { ids: depSel, action: "recibir" });
      toast.success(`${data.updated} boleta(s) depositada(s) en sucursal`);
      if (data.skipped) toast.info(`${data.skipped} no se pudieron procesar`);
      setDepSel([]); load();
    } catch (e2) { toast.error(apiErr(e2)); }
  };
  const abrirCierre = () => {
    api.get("/mi-dia").then(({ data }) => setDia(data)).catch((e2) => toast.error(apiErr(e2)));
  };
  const reintentar = async (e) => {
    try { await api.post(`/envios/${e.id}/reintentar-entrega`, {}); toast.success(`${e.code} vuelve a tu ruta`); load(); }
    catch (e2) { toast.error(apiErr(e2)); }
  };
  const depositarPendientes = async () => {
    if (!window.confirm("¿Depositar en sucursal todos tus envíos pendientes? Quedarán listos para reasignar a otro repartidor.")) return;
    try { const { data } = await api.post("/mi-ruta/depositar-pendientes"); toast.success(`${data.depositados} envío(s) depositado(s) en sucursal`); setDia(null); load(); }
    catch (e2) { toast.error(apiErr(e2)); }
  };
  const confirmDelivery = async (payload) => {
    try {
      const { data } = await api.post(`/envios/${comp.id}/status`, { status: "entregado", ...payload });
      toast.success("Entregado con comprobante");
      if (!avisoEntregaRemitente(data)) toast.info("Entrega registrada. El envío no tiene WhatsApp del remitente para el aviso automático.");
      setComp(null); load();
    }
    catch (e2) { toast.error(apiErr(e2)); }
  };
  const notify = (e) => {
    const base = `Hola ${e.destinatario}! Soy repartidor de Pya'elino Courier. Tu envío ${e.code} está asignado para entrega. ¿Me compartís tu ubicación para coordinar la entrega?`;
    const extra = e.tipo_servicio === "prioritario"
      ? (e.horario_entrega ? `Recibirás tu envío en el horario solicitado (${e.horario_entrega}).` : "Tu envío es prioritario y tiene preferencia de entrega.")
      : "Recibirás tu envío durante el transcurso del día.";
    openWa(waLink(e.whatsapp, `${base} ${extra}`));
  };

  return (
    <>
      <Header title="Mi Ruta" subtitle="Tus entregas de hoy" onMenu={() => setMobileOpen(true)}
        action={isDelivery && (
          <button onClick={abrirCierre} data-testid="cerrar-dia-btn"
            className="flex items-center gap-1.5 text-xs font-semibold text-white bg-[#0A1F44] hover:bg-[#132D5C] rounded-lg px-3 py-2">
            <Flag className="h-4 w-4" /> Cerrar mi día
          </button>
        )} />
      <main className="p-4 sm:p-6 max-w-xl mx-auto space-y-4">
        {/* Resumen */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl border border-[#E2E8F0] p-3 shadow-sm text-center" data-testid="miruta-activos">
            <div className="text-2xl font-extrabold text-[#0A1F44]">{activos.length}</div>
            <div className="text-[11px] text-[#64748B] font-medium">Pendientes</div>
          </div>
          <div className="bg-white rounded-xl border border-[#E2E8F0] p-3 shadow-sm text-center" data-testid="miruta-hoy">
            <div className="text-2xl font-extrabold text-emerald-600">{entregadosHoy.length}</div>
            <div className="text-[11px] text-[#64748B] font-medium">Entregas hoy</div>
          </div>
          <div className="bg-white rounded-xl border border-blue-200 bg-blue-50/60 p-3 shadow-sm text-center" data-testid="miruta-cobrado-hoy">
            <div className="text-base font-extrabold text-[#0A1F44] leading-tight mt-1">{formatGs(cobradoHoy)}</div>
            <div className="text-[11px] text-blue-800 font-medium">Cobrado hoy</div>
          </div>
          <div className="bg-white rounded-xl border border-amber-200 bg-amber-50/60 p-3 shadow-sm text-center" data-testid="miruta-rendir">
            <div className="text-base font-extrabold text-[#0A1F44] leading-tight mt-1">{formatGs(stats?.mi_monto_a_rendir || 0)}</div>
            <div className="text-[11px] text-amber-800 font-medium">A rendir</div>
          </div>
        </div>

        {/* Boletas asignadas para retiro (retirar y depositar en sucursal de origen) */}
        {retiroEnvios.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-[#64748B]">Boletas para retirar ({retiroEnvios.length})</h3>
            {retiroEnvios.map((e) => (
              <div key={e.id} className="bg-white rounded-xl border border-amber-200 p-4 shadow-sm fade-up" data-testid={`ruta-retiro-envio-${e.code}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <span className="font-mono font-bold text-xs px-2 py-0.5 rounded bg-slate-100 text-[#0A1F44] border border-slate-200">{e.code}</span>
                    <div className="font-bold text-[#0A1F44] mt-1.5">{e.destinatario}</div>
                    <div className="flex items-center gap-1 text-sm text-[#64748B]"><MapPin className="h-3.5 w-3.5" /> {e.ciudad}</div>
                  </div>
                  <StatusBadge status={e.status} />
                </div>
                <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  Confirmá el flete al retirar (por defecto {formatGs(40000)}). El aviso al destinatario se habilita después.
                </div>
                <button onClick={() => setCfRetiro(e)} data-testid={`ruta-retirar-${e.code}`} className="mt-3 w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-white bg-[#F5A623] rounded-lg py-2.5">
                  <BadgeDollarSign className="h-4 w-4" /> {e.status === "solicitado" ? "Confirmar flete y retirar" : "Confirmar flete"}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Boletas retiradas por mí, listas para depositar en sucursal (acción en bloque) */}
        {paraDepositar.length > 0 && (
          <div className="space-y-3" data-testid="miruta-depositar">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-[#64748B]">Para depositar en sucursal ({paraDepositar.length})</h3>
              <button onClick={depositar} disabled={depSel.length === 0} data-testid="depositar-bulk-btn"
                className="flex items-center gap-1.5 text-xs font-semibold text-white bg-[#0A1F44] rounded-lg px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed">
                <Building2 className="h-4 w-4" /> Depositar ({depSel.length})
              </button>
            </div>
            {paraDepositar.map((e) => (
              <label key={e.id} data-testid={`dep-item-${e.code}`} className="flex items-center gap-3 bg-white rounded-xl border border-[#E2E8F0] p-4 shadow-sm cursor-pointer">
                <input type="checkbox" checked={depSel.includes(e.id)} onChange={() => toggleDep(e.id)} data-testid={`dep-check-${e.code}`} className="h-4 w-4 rounded border-slate-300 accent-[#0A1F44]" />
                <div className="flex-1 min-w-0">
                  <span className="font-mono font-bold text-xs px-2 py-0.5 rounded bg-slate-100 text-[#0A1F44] border border-slate-200">{e.code}</span>
                  <div className="font-bold text-[#0A1F44] mt-1">{e.destinatario}</div>
                  <div className="flex items-center gap-1 text-sm text-[#64748B]"><MapPin className="h-3.5 w-3.5" /> {e.ciudad} · origen {e.sucursal_origen}</div>
                </div>
                <StatusBadge status={e.status} />
              </label>
            ))}
          </div>
        )}

        {/* Entregas activas: primero las asignadas a mí */}
        <input value={q} onChange={(ev) => setQ(ev.target.value)} placeholder="Buscar en mi ruta: código, destinatario, ciudad…" data-testid="miruta-search"
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#3B82F6]" />
        <div className="space-y-3">
          {!isDelivery && <h3 className="text-sm font-semibold text-[#0A1F44]" data-testid="miruta-asignados-mi">Asignados a mí ({activosMios.length})</h3>}
          {activos.map((e, idx) => (
            <React.Fragment key={e.id}>
            {!isDelivery && idx === activosMios.length && <h3 className="text-sm font-semibold text-[#64748B] pt-3" data-testid="miruta-otros">Otros envíos en reparto ({activosOtros.length})</h3>}
            <div className={`bg-white rounded-xl border p-4 shadow-sm fade-up ${e.repartidor_id === user?.id ? "border-blue-300" : "border-[#E2E8F0]"}`} data-testid={`ruta-card-${e.code}`}>
              <div className="flex items-start justify-between">
                <div>
                  <span className="font-mono font-bold text-xs px-2 py-0.5 rounded bg-slate-100 text-[#0A1F44] border border-slate-200">{e.code}</span>
                  <div className="font-bold text-[#0A1F44] mt-1.5">{e.destinatario}</div>
                  <div className="flex items-center gap-1 text-sm text-[#64748B]"><MapPin className="h-3.5 w-3.5" /> {e.ciudad}</div>
                </div>
                <StatusBadge status={e.status} />
              </div>
              <div className="mt-3 flex items-center justify-between rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                <span className="text-xs text-[#64748B]">{MODALIDAD_LABEL[e.modalidad]}</span>
                <span className="text-sm font-bold text-[#0A1F44]">Cobrar {formatGs(e.cobro_destinatario)}</span>
              </div>
              {e.tipo_servicio === "prioritario" && (
                <div className="mt-2 flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-300 px-3 py-2 text-xs font-bold text-amber-800" data-testid={`ruta-prioritario-${e.code}`}>
                  <Zap className="h-4 w-4" /> PRIORITARIO{e.horario_entrega ? ` · Entregar ${e.horario_entrega}` : ""}
                </div>
              )}
              <div className="mt-3 grid grid-cols-3 gap-2">
                {e.flete_confirmado ? (
                  <button onClick={() => notify(e)} data-testid={`ruta-wa-${e.code}`} className="flex items-center justify-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg py-2.5">
                    <MessageCircle className="h-4 w-4" /> Avisar
                  </button>
                ) : (
                  <button onClick={() => setCfRetiro(e)} data-testid={`ruta-flete-${e.code}`} className="flex items-center justify-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg py-2.5">
                    <BadgeDollarSign className="h-4 w-4" /> Flete
                  </button>
                )}
                {e.status === "asignado_repartidor" ? (
                  <button onClick={() => tomar(e)} data-testid={`ruta-tomar-${e.code}`} className="flex items-center justify-center gap-1.5 text-xs font-semibold text-white bg-[#F5A623] rounded-lg py-2.5 col-span-2">
                    <Truck className="h-4 w-4" /> Salir a repartir
                  </button>
                ) : (
                  <button onClick={() => setComp(e)} data-testid={`ruta-entregar-${e.code}`} className="flex items-center justify-center gap-1.5 text-xs font-semibold text-white bg-emerald-500 rounded-lg py-2.5 col-span-2">
                    <PackageCheck className="h-4 w-4" /> Entregar
                  </button>
                )}
              </div>
              <button onClick={() => setInc(e)} data-testid={`ruta-incidencia-${e.code}`} className="mt-2 w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-white bg-rose-500 hover:bg-rose-600 rounded-lg py-2.5">
                <AlertTriangle className="h-4 w-4" /> Reportar incidencia
              </button>
            </div>
            </React.Fragment>
          ))}
          {activos.length === 0 && (
            <div className="bg-white rounded-xl border border-[#E2E8F0] p-8 shadow-sm text-center text-[#64748B]">
              <CheckCircle2 className="h-8 w-8 mx-auto text-emerald-400 mb-2" />
              No tenés entregas pendientes. ¡Buen trabajo!
            </div>
          )}
        </div>

        {misIncidencias.length > 0 && (
          <div className="space-y-2" data-testid="miruta-incidencias">
            <h3 className="text-sm font-semibold text-rose-600 mt-4">Mis incidencias ({misIncidencias.length})</h3>
            {misIncidencias.map((e) => (
              <div key={e.id} className="bg-white rounded-lg border border-rose-200 p-3 flex items-center justify-between gap-3 shadow-sm">
                <div className="min-w-0">
                  <span className="font-mono font-bold text-xs">{e.code}</span> <span className="text-sm font-semibold text-[#0A1F44]">{e.destinatario}</span>
                  <div className="text-xs text-[#64748B] truncate">{e.incidente?.motivo_label}{e.incidente?.reagendar_fecha ? ` · ${e.incidente.reagendar_fecha}` : ""}</div>
                </div>
                <button onClick={() => reintentar(e)} data-testid={`ruta-reintentar-${e.code}`} className="shrink-0 text-xs font-semibold px-3 py-2 rounded-lg bg-[#F5A623] text-[#0A1F44]">Reintentar entrega</button>
              </div>
            ))}
          </div>
        )}

        {retiros.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-[#64748B] mb-2 mt-4">Mis retiros ({retiros.length})</h3>
            <div className="space-y-3">
              {retiros.map((r) => (
                <div key={r.id} className="bg-white rounded-xl border border-[#E2E8F0] p-4 shadow-sm" data-testid={`ruta-retiro-${r.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center"><Truck className="h-5 w-5 text-[#3B82F6]" /></div>
                      <div>
                        <div className="font-bold text-[#0A1F44]">{r.cliente_nombre}</div>
                        <div className="text-xs text-[#64748B] flex items-center gap-1"><MapPin className="h-3 w-3" /> {r.direccion} · {r.ciudad}</div>
                      </div>
                    </div>
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-50 text-blue-700">{r.status}</span>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <button onClick={() => window.open(waLink(r.whatsapp, `Hola ${r.cliente_nombre}! Soy de Pya'elino Courier, paso a retirar tu paquete en ${r.direccion}.`), "_blank")} data-testid={`ruta-retiro-wa-${r.id}`} className="flex items-center justify-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg py-2.5">
                      <MessageCircle className="h-4 w-4" /> Avisar
                    </button>
                    <Link to="/app/retiros" className="flex items-center justify-center gap-1.5 text-xs font-semibold text-white bg-[#0A1F44] rounded-lg py-2.5">Gestionar retiro</Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {entregadosHoy.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-[#64748B] mb-2 mt-4">Entregado hoy</h3>
            <div className="space-y-2">
              {entregadosHoy.map((e) => (
                <div key={e.id} className="bg-white rounded-lg border border-[#E2E8F0] p-3 flex items-center justify-between shadow-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                    <span className="text-sm font-semibold text-[#0A1F44]">{e.destinatario}</span>
                    <span className="text-xs text-[#64748B]">{e.ciudad}</span>
                  </div>
                  <span className="text-sm font-bold text-[#0A1F44]">{formatGs(e.monto_cobrado || 0)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      <ConfirmFlete envio={cfRetiro} onClose={() => setCfRetiro(null)} onDone={onRetiroConfirmado} />
      <ComprobanteModal open={!!comp} envio={comp} onClose={() => setComp(null)} onConfirm={confirmDelivery} />
      <IncidenciaModal envio={inc} onClose={() => setInc(null)} onDone={load} />

      <Dialog open={!!dia} onOpenChange={() => setDia(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="cerrar-dia-modal">
          <DialogHeader><DialogTitle className="font-display text-center">Cierre del día</DialogTitle></DialogHeader>
          {dia && (
            <div className="space-y-3">
              <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-4 py-3" data-testid="cd-entregas">
                <span className="text-sm font-medium text-[#64748B]">Entregas</span>
                <span className="text-xl font-extrabold text-[#0A1F44] tabular-nums">{dia.entregas}</span>
              </div>
              <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-4 py-3" data-testid="cd-retiros">
                <span className="text-sm font-medium text-[#64748B]">Retiros</span>
                <span className="text-xl font-extrabold text-[#0A1F44] tabular-nums">{dia.retiros}</span>
              </div>
              <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-4 py-3" data-testid="cd-monto">
                <span className="text-sm font-semibold text-amber-800">Monto a rendir</span>
                <span className="text-xl font-extrabold text-[#0A1F44] tabular-nums">{formatGs(dia.monto_a_rendir)}</span>
              </div>
              <p className="text-xs text-[#64748B] text-center">Presentá este resumen al rendir en tu sucursal.</p>
              {(activosMios.length + misIncidencias.length) > 0 && (
                <button onClick={depositarPendientes} data-testid="depositar-pendientes-btn" className="w-full flex items-center justify-center gap-2 rounded-lg bg-[#0A1F44] text-white py-2.5 text-sm font-semibold">
                  <Building2 className="h-4 w-4" /> Depositar en sucursal mis {activosMios.length + misIncidencias.length} pendiente(s)
                </button>
              )}
              <button onClick={() => setDia(null)} data-testid="cerrar-dia-cerrar-btn" className="w-full rounded-lg border border-slate-300 py-2 text-sm font-medium">Cerrar</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
